/**
 * Shared paper/margin/font-size layout math for handwritten-note text.
 *
 * Used by both the live Fabric preview ({@link useHandwrittenNotePreview}'s `render()`) and the
 * fit-measurement check (`measureHandwrittenMessage`) — kept in one place deliberately so the two can
 * never disagree about what fits. See `doc/guide/GUIDE_dynamic_message_limit_per_font.md` (TK-12497).
 *
 * The safe area (writable inset) is an explicit input, not hardcoded — this module is not note-only.
 * `DEFAULT_HANDWRITTEN_NOTE_SAFE_AREA_INCHES` below is just the handwritten-note default; a caller
 * measuring an envelope, a different card surface, or anything else with its own margins passes its own
 * `safeAreaInches`.
 *
 * **HOMS:** Prefer resolving margins via `stationeryPreviewLayoutMeta.ts` (`resolveCardFrontSafeAreaInchesFromPreviewMeta`,
 * `resolveEnvelopeFaceSafeAreaInchesFromPreviewMeta`) rather than reading {@link DEFAULT_HANDWRITTEN_NOTE_SAFE_AREA_INCHES}
 * directly. Those resolvers merge partial `preview_images[].meta.safeAreaInches` with this default object, then
 * apply product minimum floors. {@link SafeAreaInches} is the wire shape for a fully resolved inset.
 */

/**
 * Working-canvas width (CSS px) the live preview caps rendering at; also used as the reference width for
 * resolution-independent fit measurement (no real bitmap is loaded in that context).
 */
export const MAX_WORKING_WIDTH = 1200

/** Top text inset (inches) — the handwritten-note default; not a hardcoded constraint. */
export const HANDWRITTEN_TOP_MARGIN_INCHES = 0.75

/** Bottom text inset (inches) — the handwritten-note default; not a hardcoded constraint. */
export const HANDWRITTEN_BOTTOM_MARGIN_INCHES = 0.5

/** Left and right text inset (inches) — the handwritten-note default; not a hardcoded constraint. */
export const DEFAULT_UNIFORM_TEXT_MARGIN_INCHES = 0.5

/**
 * Envelope-face writable inset top (inches) when preview meta omits `safeAreaInches.top` (TK-13833).
 * Applied in `resolveEnvelopeFaceSafeAreaInchesFromPreviewMeta`; card/note use {@link HANDWRITTEN_TOP_MARGIN_INCHES}.
 */
export const DEFAULT_ENVELOPE_FACE_SAFE_AREA_TOP_INCHES = 0.5

/** Default font size as a ratio of canvas width — overridable per font (see TK-12497 "per-font size ratio"). */
export const DEFAULT_FONT_SIZE_RATIO = 0.035

/** Default line height (Fabric's multiplier of font size) — overridable per font. */
export const DEFAULT_LINE_HEIGHT_RATIO = 1.25

/**
 * Fraction of the computed writable height actually treated as available when checking whether a message
 * fits (see `measureHandwrittenMessage`). Defaults to `1` (no shrinkage) — an earlier `0.85` guess meant to
 * leave headroom for descenders (letters like `p`/`g`) turned out to be too conservative: real handwritten
 * test letters showed descenders already sit inside the writable box at the current margins/font sizes, so
 * the extra shrink was just costing usable space for no reason. Still overridable per call (see
 * `measureHandwrittenMessage`'s `safetyMargin` option) in case a stricter buffer is needed later — margins
 * (not this multiplier) are the next thing expected to change, once more test letters have been run — see
 * "Requirements to confirm" in `doc/guide/GUIDE_dynamic_message_limit_per_font.md`.
 */
export const DEFAULT_HANDWRITING_SAFETY_MARGIN = 1

/** A writable-area inset (inches) on each side of a surface — independent per side, not assumed uniform. */
export type SafeAreaInches = {
    top: number
    bottom: number
    left: number
    right: number
}

/**
 * The handwritten-note default safe area — matches this module's original (pre-refactor) hardcoded note
 * margins exactly, so omitting `safeAreaInches` preserves existing behavior. A different surface (an
 * envelope, some other card) should build its own `SafeAreaInches` instead of reusing this one.
 *
 * **HOMS:** Used internally when meta omits `safeAreaInches`; server APIs should still run
 * `resolveCardFrontSafeAreaInchesFromPreviewMeta` / `resolveEnvelopeFaceSafeAreaInchesFromPreviewMeta` so
 * callers receive post-merge, post-floor values (see `stationeryPreviewLayoutMeta.ts` module doc).
 */
export const DEFAULT_HANDWRITTEN_NOTE_SAFE_AREA_INCHES: SafeAreaInches = {
    top: HANDWRITTEN_TOP_MARGIN_INCHES,
    bottom: HANDWRITTEN_BOTTOM_MARGIN_INCHES,
    left: DEFAULT_UNIFORM_TEXT_MARGIN_INCHES,
    right: DEFAULT_UNIFORM_TEXT_MARGIN_INCHES,
}

export type HandwrittenNoteTextLayout = {
    /** Left padding (px) — clamped with `rightPadding` so both never exceed canvas width. */
    leftPadding: number
    /** Right padding (px) — clamped with `leftPadding` so both never exceed canvas width. */
    rightPadding: number
    /** Top padding (px) — clamped with `bottomPadding` so both never exceed canvas height. */
    topPadding: number
    /** Bottom padding (px) — clamped with `topPadding` so both never exceed canvas height. */
    bottomPadding: number
    /** Text box width (px) after left/right padding. */
    textWidth: number
    /** Font size (px) — proportional to `canvasWidth`, floored so tiny canvases stay legible. */
    fontSize: number
    /** Line height (Fabric's multiplier of font size) — per-font, see {@link DEFAULT_LINE_HEIGHT_RATIO}. */
    lineHeight: number
}

export type ComputeTextLayoutOptions = {
    /**
     * Lets a specific font render larger/smaller than {@link DEFAULT_FONT_SIZE_RATIO} — the per-font lever
     * called for in TK-12497 (fonts differ in apparent size, so a fixed ratio for every font under- or
     * over-estimates capacity for fonts drawn larger/smaller than average).
     */
    fontSizeRatio?: number
    /** Lets a specific font use tighter/looser line spacing than {@link DEFAULT_LINE_HEIGHT_RATIO}. */
    lineHeightRatio?: number
    /**
     * The writable-area inset (inches) on each side. Defaults to
     * {@link DEFAULT_HANDWRITTEN_NOTE_SAFE_AREA_INCHES} — override this to lay out text on any other
     * surface (envelope, different card size, etc.) with its own margins.
     */
    safeAreaInches?: SafeAreaInches
}

/**
 * Scales a pair of paddings down proportionally (never favoring one over the other) when their sum would
 * eat more than `availableSpace` minus a 1px floor — e.g. a very small paper size relative to a fixed
 * margin. Applied independently to the left/right pair and the top/bottom pair.
 */
function clampPaddingPairToAvailableSpace(
    paddingA: number,
    paddingB: number,
    availableSpace: number
): [number, number] {
    const minimumRemainingSpace = 1
    const maximumTotalPadding = Math.max(0, availableSpace - minimumRemainingSpace)
    const totalPadding = paddingA + paddingB
    if (totalPadding > maximumTotalPadding && totalPadding > 0) {
        const scaleFactor = maximumTotalPadding / totalPadding
        return [paddingA * scaleFactor, paddingB * scaleFactor]
    }
    return [paddingA, paddingB]
}

/**
 * Computes the writable text box + font size for a `paperSizeInches` surface rendered on a working canvas
 * `canvasWidth` × `canvasHeight` px. `canvasWidth`/`canvasHeight` come from the real bitmap in a live
 * preview, or from a fixed reference width (see {@link MAX_WORKING_WIDTH}) when measuring fit with no
 * bitmap loaded.
 *
 * Pass `options.safeAreaInches` to lay out text on any surface's own margins — the default only applies to
 * handwritten notes (see {@link DEFAULT_HANDWRITTEN_NOTE_SAFE_AREA_INCHES}).
 */
export function computeHandwrittenNoteTextLayout(
    paperSizeInches: { width: number; height: number },
    canvasWidth: number,
    canvasHeight: number,
    options: ComputeTextLayoutOptions = {}
): HandwrittenNoteTextLayout {
    const {
        fontSizeRatio = DEFAULT_FONT_SIZE_RATIO,
        lineHeightRatio = DEFAULT_LINE_HEIGHT_RATIO,
        safeAreaInches = DEFAULT_HANDWRITTEN_NOTE_SAFE_AREA_INCHES,
    } = options

    const rawLeftPadding = (safeAreaInches.left / paperSizeInches.width) * canvasWidth
    const rawRightPadding = (safeAreaInches.right / paperSizeInches.width) * canvasWidth
    const [leftPadding, rightPadding] = clampPaddingPairToAvailableSpace(
        rawLeftPadding,
        rawRightPadding,
        canvasWidth
    )

    const rawTopPadding = (safeAreaInches.top / paperSizeInches.height) * canvasHeight
    const rawBottomPadding = (safeAreaInches.bottom / paperSizeInches.height) * canvasHeight
    const [topPadding, bottomPadding] = clampPaddingPairToAvailableSpace(
        rawTopPadding,
        rawBottomPadding,
        canvasHeight
    )

    const textWidth = Math.max(1, canvasWidth - leftPadding - rightPadding)
    /** Scale with canvas width only — not tied to margin. */
    const fontSize = Math.max(14, Math.round(canvasWidth * fontSizeRatio))

    return {
        leftPadding,
        rightPadding,
        topPadding,
        bottomPadding,
        textWidth,
        fontSize,
        lineHeight: lineHeightRatio,
    }
}
